import {
  SubscriptionResponse,
  User,
  PlanSlugType,
  ReleaseData,
  Account,
  Profile,
  Integrations,
  WorkoutSettings,
} from '@sworkit/api';
import { firebaseAuth, firebaseFunctions, fst as firestore } from './firebase';
import { removeUndefinedAndNaNRecursive } from './utils';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { User as FirebaseAuthUser } from 'firebase/auth';

export const defaultAge = 35;
export const defaultBirthYear = new Date().getFullYear() - defaultAge;
export function getLoggedInUserId() {
  return firebaseAuth?.currentUser?.uid;
}

export function getLoggedInUserEmail() {
  return firebaseAuth?.currentUser?.email;
}

export const hasLegacyVirginPulseSignIn = (user: FirebaseAuthUser) => {
  const virginPulseProvider = user?.providerData.find((providerItem) =>
    providerItem.providerId?.includes('virgin-pulse'),
  );
  const personifyHealthProvider = user?.providerData.find((providerItem) =>
    providerItem.providerId?.includes('personify-health'),
  );
  return Boolean(virginPulseProvider && !personifyHealthProvider);
};

export async function validate() {
  const uid = getLoggedInUserId();
  if (uid && firebaseFunctions) {
    const verifyUser = httpsCallable(firebaseFunctions, 'verifyUser');
    const result = await verifyUser({ uid });
    const res = result.data;
    if (typeof res !== 'undefined') {
      return res as SubscriptionResponse;
    }
  }
  return Promise.reject('something went wrong with validation');
}

export function getUserDocListener(callback: (userDoc: User) => void) {
  const uid = getLoggedInUserId();
  if (!firestore || !uid) return;
  return onSnapshot(doc(firestore, `users/${uid}`), (doc) =>
    callback(doc.data() as User),
  );
}

export async function firestoreUpdateUserPlanSlug(newPlanSlug: PlanSlugType) {
  const uid = getLoggedInUserId();
  if (!uid || !firestore) return;
  const newPlan: Partial<User> = {
    plan: {
      currentPlanSlug: newPlanSlug,
    },
  };
  return setDoc(doc(firestore, `users/${uid}`), newPlan, { merge: true });
}

export function getProfileWithWeight(profile: Partial<Profile>) {
  if (
    typeof profile?.displayWeight !== 'undefined' &&
    profile?.displayWeight !== null
  ) {
    let weightInLbs = profile.displayWeight;
    if (profile.weightType === 'kg') {
      weightInLbs = Math.round(profile.displayWeight * 2.20462);
    }
    profile.weightInLbs = weightInLbs;
  }
  return profile;
}

export async function updateProfile(profile: Partial<Profile>) {
  const cleanProfile = removeUndefinedAndNaNRecursive(profile);
  const uid = getLoggedInUserId();
  if (!uid || !firestore) return;
  const update = { profile: getProfileWithWeight(cleanProfile) };
  if (Object.keys(update.profile).length) {
    try {
      await setDoc(doc(firestore, `/users/${uid}`), update, { merge: true });
    } catch (error) {
      console.error("Failed to update profile:", error);
    }
  } else {
    console.debug("no profile update, do not send an empty object to firestore as update");
  }
}

export function updateAccount(account: Partial<Account>) {
  const uid = getLoggedInUserId();
  if (!uid || !firestore || !account) return Promise.resolve();
  const accountUpdate = { ...account, uid };
  const update = removeUndefinedAndNaNRecursive({ account: accountUpdate });
  return setDoc(doc(firestore, `/users/${uid}`), update, { merge: true });
}

export function updateCurrentAccountStatus(
  currentAccountStatus: Partial<SubscriptionResponse>,
) {
  const uid = getLoggedInUserId();
  if (!uid || !firestore || !currentAccountStatus) return Promise.resolve();
  const update = removeUndefinedAndNaNRecursive({
    currentAccountStatus,
  } as User);
  return setDoc(doc(firestore, `/users/${uid}`), update, { merge: true });
}

export function saveWorkoutSettings(workoutSettings: Partial<WorkoutSettings>) {
  const uid = getLoggedInUserId();
  if (!firestore || !uid) return;
  const update: Partial<User> = { workoutSettings };
  setDoc(doc(firestore, `users/${uid}`), update, { merge: true });
}

export function enjoyingWorkoutsAsked() {
  const uid = getLoggedInUserId();
  if (!uid || !firestore) return;
  const update: Partial<User> = {
    meta: {
      lastEnjoyingWorkoutsAsked: Date.now(),
    },
  };
  setDoc(doc(firestore, `users/${uid}`), update, { merge: true });
}

export function shareYearInReviewAsked() {
  const uid = getLoggedInUserId();
  if (!uid || !firestore) return;
  const update: Partial<User> = {
    meta: {
      lastShareYearInReviewAsked: new Date().getFullYear(),
    },
  };
  setDoc(doc(firestore, `users/${uid}`), update, { merge: true });
}

export function updateIntegrations(integrations: Partial<Integrations>) {
  const uid = getLoggedInUserId();
  if (!firestore || !uid) return;
  setDoc(doc(firestore, `users/${uid}`), { integrations }, { merge: true });
}

export function saveWorkoutReleaseData(releaseData: ReleaseData) {
  const uid = getLoggedInUserId();
  if (!firestore || !uid) return;
  const update: Partial<User> = {
    meta: { releaseData },
  };
  return setDoc(doc(firestore, `users/${uid}`), update, { merge: true });
}

export const defaultUserData: User = {
  account: {
    env: 'prod',
    authType: null,
    affiliateId: null,
    signUpDate: null,
    orgId: null,
    locale: null,
    email: null,
    emailPreference: null,
    freeWorkoutLimit: null,
    nextOnboardingPrompt: null,
    onboardingChoice: null,
    onboardingChoiceSecondary: null,
    pinnedCollection: null,
    homeDisplayCardChoice: null,
  },
  allTimeStats: {
    activeDays: 0,
    caloriesBurned: 0,
    totalSessions: 0,
    totalWorkoutTime: 0,
    workoutStreakWeeks: 0,
    lastWorkoutCreatedOn: null,
    lastWorkoutTimestampMillis: 0,
    lastWorkoutCollectionSlug: null,
    lastWorkoutSlug: null,
    streakWeekStart: null,
    lastContentViewedOn: null,
    lastContentTimestampMillis: null,
    lastContentCollectionSlug: null,
    lastContentSlug: null,
  },
  integrations: {},
  currentAccountStatus: {
    auto_renew_status: false,
    is_active_subscriber: false,
    is_trial_period: false,
    is_paying_customer: false,
    expires_date: 0,
    original_purchase_date: 0,
    purchase_date: 0,
    product_id: null,
    source: 'none',
    subscription_status: 'none',
    display_source: 'Sworkit',
    subscription_translation_id: 'NO_SUBSCRIPTION',
    email: null,
    email_verified: false,
    email_verification_required: false,
    uid: null,
  },
  profile: {
    activityLevel: null,
    displayWeight: 175,
    weightInLbs: 175,
    weightType: 'lb',
    birthYear: defaultBirthYear,
    firstName: null,
    gender: null,
    goal: null,
    help: null,
    lastName: null,
    photo: null,
    weeklyGoal: 3,
    dailyStepGoal: 10000,
    distanceUnit: 'miles',
    workoutInterest: null,
    injuryArea: null,
    painScale: null,
    workoutMinutesGoal: 150,
  },
  meta: {},
  entitlements: {},
  workoutSettings: {},
  plan: { currentPlanSlug: null },
};
